// Imports
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMicrochip, faSignal, faProjectDiagram, faChartBar, faShieldAlt, faCloud, 
  faBrain, faLayerGroup, faMobileAlt, faBell, faCogs, faNetworkWired, faServer, faUsers, faGaugeHigh  
} from '@fortawesome/free-solid-svg-icons';

// Component imports
import ThinkyIOTSCADA from './ThinkyIOTSCADA'; 
import ThinkyIOTDeviceManagement from './ThinkyIOTDeviceManagement'; 
import ThinkyIOTTelemetryDataIngestion from './ThinkyIOTTelemetryDataIngestion'; 
import ThinkyIOTAutomationEngine from './ThinkyIOTAutomationEngine'; 
import ThinkyIOTDashboard from './ThinkyIOTDashboard'; 
import ThinkyIOTSecurityGovernance from './ThinkyIOTSecurityGovernance'; 
import ThinkyIOTDeployment from './ThinkyIOTDeployment'; 
import ThinkyIOTNotifications from './ThinkyIOTNotifications'; 
import ThinkyIOTRPC from './ThinkyIOTRPC'; 
import ThinkyIOTGatewayIntegration from './ThinkyIOTGatewayIntegration'; 
import ThinkyIOTServerScalability from './ThinkyIOTServerScalability'; 
import ThinkyIOTUserManagement from './ThinkyIOTUserManagement'; 
import ThinkyIOTIntelligenceLayer from './ThinkyIOTIntelligenceLayer'; 
import ThinkyIOTTaaP from './ThinkyIOTTaaP'; 
import ThinkyIOTMobileFirstDesign from './ThinkyIOTMobileFirstDesign';

// Main Component
export default function ThinkyIOTHome() {
  const [activeModule, setActiveModule] = useState(null);

  const features = [
    { icon: faGaugeHigh , title: "SCADA", desc: "Modern - Supervisory Control and Data Acquisition." },
    { icon: faMicrochip, title: "Device & Asset Management", desc: "Onboard, provision, group and manage IoT devices, assets, customers, and hierarchical relationships." },
    { icon: faSignal, title: "Telemetry & Data Ingestion", desc: "Secure real-time data ingestion via MQTT, HTTP, CoAP with support for gateways and legacy protocols." },
    { icon: faProjectDiagram, title: "Rules & Automation Engine", desc: "Build visual rule chains to transform, filter, and process data; trigger alarms and external integrations." },
    { icon: faChartBar, title: "Dashboards & Visualization", desc: "Real-time, historical, and interactive dashboards with charts, maps, gauges, and SCADA-style views." },
    { icon: faShieldAlt, title: "Security & Governance", desc: "Device authentication, encrypted transport, role-based access control (RBAC), audit logs, and multi-tenant security." },
    { icon: faCloud, title: "Cloud, Edge & Hybrid Deployment", desc: "Deploy centrally, on edge devices, or hybrid with offline sync, high availability, and scalability." },
    { icon: faBell, title: "Alarms & Notifications", desc: "Define, manage, and propagate alarms based on telemetry, inactivity, or attributes with multi-channel notifications." },
    { icon: faCogs, title: "Remote Control & RPC", desc: "Interactive remote control and RPC calls to devices directly from the dashboards." },
    { icon: faNetworkWired, title: "IoT Gateway Integration", desc: "Support for Modbus, BACnet, OPC-UA, BLE, and other legacy/third-party protocols through IoT gateways." },
    { icon: faServer, title: "High Availability & Scalability", desc: "Clustered architecture with horizontal scalability, fault tolerance, and distributed storage options." },
    { icon: faUsers, title: "Multi-Tenancy & User Management", desc: "Support multiple tenants, customers, and users with flexible roles and permissions." },
    { icon: faBrain, title: "Think5Whys Intelligence Layer", desc: "Contextual reasoning, root-cause analysis, predictive insights, and decision intelligence beyond telemetry." },
    { icon: faLayerGroup, title: "Transformation-as-a-Platform", desc: "Composable IoT + AI + workflow modules designed for continuous operational transformation." },
    { icon: faMobileAlt, title: "Mobile-first Experience", desc: "Optimized UX for operators, leaders, and field teams across mobile, tablet, and desktop devices." }
  ];

  // Conditional module rendering
  const renderModule = () => {
    switch(activeModule) {
      case 'SCADA': return <ThinkyIOTSCADA onBack={() => setActiveModule(null)} />;
      case 'Device & Asset Management': return <ThinkyIOTDeviceManagement onBack={() => setActiveModule(null)} />;
      case 'Telemetry & Data Ingestion': return <ThinkyIOTTelemetryDataIngestion onBack={() => setActiveModule(null)} />;
      case 'Rules & Automation Engine': return <ThinkyIOTAutomationEngine onBack={() => setActiveModule(null)} />;
      case 'Dashboards & Visualization': return <ThinkyIOTDashboard onBack={() => setActiveModule(null)} />;
      case 'Security & Governance': return <ThinkyIOTSecurityGovernance onBack={() => setActiveModule(null)} />;
      case 'Cloud, Edge & Hybrid Deployment': return <ThinkyIOTDeployment onBack={() => setActiveModule(null)} />;
      case 'Alarms & Notifications': return <ThinkyIOTNotifications onBack={() => setActiveModule(null)} />;
      case 'Remote Control & RPC': return <ThinkyIOTRPC onBack={() => setActiveModule(null)} />;
      case 'IoT Gateway Integration': return <ThinkyIOTGatewayIntegration onBack={() => setActiveModule(null)} />;
      case 'High Availability & Scalability': return <ThinkyIOTServerScalability onBack={() => setActiveModule(null)} />;
      case 'Multi-Tenancy & User Management': return <ThinkyIOTUserManagement onBack={() => setActiveModule(null)} />;
      case 'Think5Whys Intelligence Layer': return <ThinkyIOTIntelligenceLayer onBack={() => setActiveModule(null)} />;
      case 'Transformation-as-a-Platform': return <ThinkyIOTTaaP onBack={() => setActiveModule(null)} />;
      case 'Mobile-first Experience': return <ThinkyIOTMobileFirstDesign onBack={() => setActiveModule(null)} />;
      default: return null;
    }
  };

  // Home page render
  if(activeModule) return renderModule();

  return (
    <div className="min-h-screen bg-black text-white px-4 py-8">
      <header className="mb-10 text-center">
        <h1 className="text-xl md:text-2xl font-bold text-white">📡ThinkyIOT</h1>
        <p className="mt-3 text-blue-300 mx-auto text-sm md:text-base">
          A Think5Whys Transformation-as-a-Platform inspired by ThingsBoard — from data collection to full decision intelligence.
        </p>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {features.map((f, i) => (
          <div key={i} className="bg-neutral-900 border border-neutral-800 rounded-2xl shadow-sm p-5 cursor-pointer hover:bg-neutral-800" onClick={() => setActiveModule(f.title)}>
            <div className="flex items-center gap-3 mb-3">
              <FontAwesomeIcon icon={f.icon} className="w-6 h-6 text-blue-400" />
              <h3 className="text-sm lg:text-lg font-semibold text-white">{f.title}</h3>
            </div>
            <p className="text-xs lg:text-sm text-neutral-300 leading-relaxed">{f.desc}</p>
          </div>
        ))}
      </section>

      <footer className="mt-12 text-center">
        <p className="text-xs text-neutral-500">
          © Think5Whys · ThinkyIOT · From sensing signals to transforming decisions
        </p>
      </footer>
    </div>
  );
}